import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '@/services/base.query';
import { LegalDocumentsResponse } from '@/services/api.types';

// Map the UI language onto the wire language token this surface expects.
//
// The BE matches `legal_document_content.lang` EXACTLY, with no normalisation
// (data/v1/legal.go:60-72), and those rows hold "English"/"Danish" — seed.go:430
// says so outright ("lang stored as \"English\"/\"Danish\" (aligned to the FE)").
// So an ISO code silently matches nothing: the batch-level integration gate measured
// lang=da -> 0 documents and lang=Danish -> 2, and because ADR-5 correctly skips a
// document whose content row is missing, the empty result looked exactly like "this
// tenant has no legal documents configured". Both sidebar entries vanished with every
// gate in both repos green.
//
// This mirrors the landed twin surface verbatim (TosAndPP/index.tsx:111). It lives
// here, beside the endpoint that speaks the convention, so the two call sites cannot
// drift from each other — the bug came from a convention that was written down in one
// place and assumed in another.
//
// ⚠️ Do NOT reach for `LanguageCodes` in constants/system.ts: its `da` entry is
// 'Dansk', the DISPLAY name, not the 'Danish' wire value. It looks like the right
// constant and is not.
//
// Unifying both surfaces on ISO codes is the better end state, but it needs a data
// migration plus a change to the already-landed consent gate — registered as
// DEF-004-06, deliberately out of scope here.
export const legalContentLang = (i18nLanguage: string): string =>
  i18nLanguage === 'en' ? 'English' : 'Danish';

// Logged-in legal-document read surface (batch 004, ADR-5). Replaces the old BFF
// `POST /user/accepted/policy/file`, which used to live in auth.api.ts and was the
// last consumer of `tenantInfo.config.user_policy` — the thing that blocked the
// tenant-header teardown (DEF-001-01).
const legalApi = createApi({
  reducerPath: 'legalApi',
  // base.query (unauthenticated wrapper) + an EXPLICIT Bearer, exactly as the
  // endpoint this replaces did (it took `access` as an argument). Deliberately NOT
  // authed.query: this is fetched from bootstrapConfig(), which runs BEFORE the
  // first render, so the token is not in the store yet — authed.query's
  // prepareHeaders would find nothing, and its 401 interceptor would then try to
  // renew and bounce to /login, which on the login page is a redirect loop.
  baseQuery,
  endpoints: builder => ({
    // GET /v1/legal/documents?lang= → [{type, versionId, publishedAt, url}].
    //
    // A MUTATION, not a query, even though it is a GET. RTK-Query caches queries,
    // and re-serving this one from cache would replay a stale storage url on the
    // second dialog open — the consent gate already hit this and wrote the
    // conclusion down (auth.api.ts:74-77). The list has exactly one owner (the
    // `legal` slice, populated via matchFulfilled), so there is nothing for a
    // query's cache to be useful for here anyway.
    legalDocuments: builder.mutation<
      LegalDocumentsResponse,
      { lang: string; token: string }
    >({
      query: ({ lang, token }) => ({
        url: '/v1/legal/documents',
        method: 'GET',
        params: { lang },
        headers: { Authorization: `Bearer ${token}` },
      }),
    }),
  }),
});

export const { useLegalDocumentsMutation } = legalApi;
export default legalApi;
